/**
 * Public Lead Capture — AI chatbot.
 * Embedded on tenant websites via an iframe (no auth). Runs the tenant's
 * configured lead-form AI agent (Agents module) through /public/chat/:slug; the
 * agent answers from its knowledge and the backend captures the visitor's
 * contact into a lead when it detects a phone/email. Rate-limited server-side.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { sanitizeAssistantText } from '@/utils/assistant-text.util';
import { consumeSseResponse } from '@/utils/sse-stream.util';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TenantBranding {
  name: string;
  subdomain: string;
  branding: { logoUrl?: string; primaryColor?: string; secondaryColor?: string } | null;
  /** Widget copy overrides (settings.leadForm). Absent = use the defaults below. */
  botName?: string;
  welcomeMessage?: string;
  statusText?: string;
}

// Built-in defaults — the single source of truth for "what the widget shows when
// the tenant hasn't overridden it". The admin form shows these same strings as
// input placeholders so an empty box visibly means "using this".
const DEFAULT_STATUS_TEXT = 'Typically replies instantly';
const DEFAULT_SECONDARY_COLOR = '#818cf8';
const defaultWelcome = (name: string) =>
  `Hi! 👋 Welcome to ${name}. Ask me anything — or share your name and number and we'll get back to you.`;

interface ChatMessage {
  /** Stable identity for React keys AND for targeting the in-progress bot
   *  bubble while its text streams in. Deliberately not the array index: the
   *  index of a not-yet-committed message can't be read back synchronously
   *  (see `send`), which is exactly what split one streamed reply across
   *  several bubbles. */
  id: string;
  role: 'bot' | 'user';
  text: string;
}

/** Monotonic id source for chat bubbles. A plain counter rather than
 *  crypto.randomUUID() — this widget is embedded on arbitrary third-party
 *  sites, and a counter needs no secure-context / browser-support caveats. */
let msgSeq = 0;
const nextMsgId = (): string => `m${++msgSeq}`;

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * The session id is MINTED AND SIGNED BY THE SERVER (see backend
 * utils/webchat-session-token.util.ts) and returned on every reply — the client
 * no longer generates one. A client-generated id can't carry the server's HMAC,
 * so it earns only IP-keyed rate limiting rather than its own session budget;
 * sending nothing on the first turn is what asks the server for a real one.
 */

// ─── Component ───────────────────────────────────────────────────────────────

export default function LeadCapture() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const [tenant, setTenant] = useState<TenantBranding | null>(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [tenantError, setTenantError] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [botTyping, setBotTyping] = useState(false);

  const endRef = useRef<HTMLDivElement | null>(null);
  const startedRef = useRef(false);
  // Empty until the server issues one on the first reply; sent back verbatim on
  // every subsequent turn so the whole conversation groups under one session.
  const sessionIdRef = useRef<string>('');
  const primaryColor = tenant?.branding?.primaryColor || '#4f46e5';
  // Was hardcoded to DEFAULT_SECONDARY_COLOR in the header gradient, so the
  // secondaryColor a tenant sets under Settings → Organization was saved but
  // never rendered anywhere. Falls back to the same constant, so a tenant who
  // never set one sees no visual change.
  const secondaryColor = tenant?.branding?.secondaryColor || DEFAULT_SECONDARY_COLOR;
  // Display name for the widget: the tenant's bot-name override, else their org name.
  const displayName = tenant?.botName || tenant?.name || '';

  // Load tenant branding
  useEffect(() => {
    if (!tenantSlug) return;
    fetch(`${API_BASE}/public/tenant/${tenantSlug}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success) setTenant(data.tenant);
        else setTenantError(true);
      })
      .catch(() => setTenantError(true))
      .finally(() => setTenantLoading(false));
  }, [tenantSlug]);

  // Opening greeting (once). The AI answers from the visitor's first real message.
  useEffect(() => {
    if (tenant && !startedRef.current) {
      startedRef.current = true;
      setMessages([
        {
          id: nextMsgId(),
          role: 'bot',
          text: tenant.welcomeMessage || defaultWelcome(tenant.botName || tenant.name),
        },
      ]);
    }
  }, [tenant]);

  // Auto-scroll to latest
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, botTyping]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || botTyping) return;

    // Prior turns become conversationHistory; the new line is `message`.
    const history = messages.map((m) => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    setMessages((m) => [...m, { id: nextMsgId(), role: 'user', text }]);
    setInput('');
    setBotTyping(true);

    // The bot's bubble is created lazily on the first streamed token, then
    // targeted BY ID for every later delta.
    //
    // `botStarted` must be assigned synchronously here, NOT inside a
    // setMessages updater: React defers updaters, and SSE frames from one
    // network chunk are dispatched in a tight synchronous loop, so a flag set
    // inside the updater is still stale when the next frame is handled — which
    // made every early token create its own bubble ("Hi" / "Va" / "ibh" / ...)
    // instead of appending to one. Keeping the id stable and the flag outside
    // also keeps both updaters pure, so React 18 StrictMode's double-invocation
    // can't duplicate text.
    const botMessageId = nextMsgId();
    let botStarted = false;
    // What the visitor actually sees if this turn fails. Defaults to the
    // connection-problem wording, but any message the SERVER supplied wins —
    // this endpoint returns real, visitor-actionable reasons ("Too many
    // messages — please slow down.", "This chat has reached its message
    // limit.", "No AI agent is configured for this chatbot."), and telling
    // someone their network is down when they were actually rate-limited
    // sends them off debugging the wrong thing.
    let userFacingError = 'Network issue — please check your connection and try again.';

    try {
      const res = await fetch(`${API_BASE}/public/chat/${tenantSlug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversationHistory: history,
          // Omitted entirely on the first turn — that's what asks the server to
          // mint a signed one. Sending an empty string instead would look like a
          // client-invented id and cost this visitor their own session budget.
          ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}),
          // The widget's own document URL. No request header carries this: the
          // fetch is same-origin, so `Referer` is always this iframe's own URL on
          // the platform host. This is the only way the tenant's `?utm_source=...`
          // embed params reach the server to classify the lead's source.
          pageUrl: window.location.href,
        }),
      });

      if (!res.ok) {
        // Non-2xx here is a pre-stream rejection (rate limit, no agent
        // configured, tenant inactive) and still carries a JSON body.
        // `message` is the human-readable half when the payload also has an
        // `error` CODE (e.g. 'no_agent'); otherwise `error` itself is the text.
        try {
          const body = await res.json();
          const serverMsg = body?.message || body?.error;
          if (typeof serverMsg === 'string' && serverMsg) userFacingError = serverMsg;
        } catch {
          /* body wasn't JSON — keep the default connection wording */
        }
        throw new Error(`Request failed (${res.status})`);
      }

      await consumeSseResponse(res, (frame) => {
        // Adopt the server-issued (HMAC-signed) session id from ANY frame that
        // carries one — `done` on success, `error` on a failed turn — so every
        // later turn is recognised as the same trusted session even if this one
        // broke mid-stream.
        const sid = frame['sessionId'];
        if (typeof sid === 'string' && sid) sessionIdRef.current = sid;

        if (frame.type === 'token') {
          const delta = typeof frame['content'] === 'string' ? (frame['content'] as string) : '';
          if (!delta) return;
          if (!botStarted) {
            botStarted = true;
            setMessages((m) => [...m, { id: botMessageId, role: 'bot', text: delta }]);
          } else {
            setMessages((m) =>
              m.map((msg) => (msg.id === botMessageId ? { ...msg, text: msg.text + delta } : msg))
            );
          }
        } else if (frame.type === 'reset') {
          // The server discarded a partial answer (e.g. RAG streamed a few
          // tokens then failed) and is about to send a full replacement —
          // clear the bubble in place rather than appending onto stale text.
          if (botStarted) {
            setMessages((m) =>
              m.map((msg) => (msg.id === botMessageId ? { ...msg, text: '' } : msg))
            );
          }
        } else if (frame.type === 'error') {
          const msg = frame['message'];
          if (typeof msg === 'string' && msg) userFacingError = msg;
          throw new Error('stream reported an error');
        }
      });

      if (!botStarted) {
        throw new Error('No response from chat');
      }
    } catch {
      // Drop the partially-streamed bubble (if any) by id, then show the
      // failure notice — filtering by id rather than slicing by index so this
      // can't remove an unrelated message.
      setMessages((m) => [
        ...(botStarted ? m.filter((msg) => msg.id !== botMessageId) : m),
        { id: nextMsgId(), role: 'bot', text: userFacingError },
      ]);
    } finally {
      setBotTyping(false);
    }
  }, [input, botTyping, messages, tenantSlug]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  };

  // ── Render states ──────────────────────────────────────────────────────────

  if (tenantLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  if (tenantError || !tenant) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, textAlign: 'center', padding: '40px 36px' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <h2 style={{ color: '#0f172a', margin: '0 0 8px' }}>Page not found</h2>
          <p style={{ color: '#64748b', margin: 0 }}>
            This chat is no longer available or the link is incorrect.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Header */}
        <div
          style={{
            ...styles.header,
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
          }}
        >
          {tenant.branding?.logoUrl ? (
            <img src={tenant.branding.logoUrl} alt={displayName} style={styles.headerLogo} />
          ) : (
            <div style={styles.headerAvatar}>{displayName.charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{displayName}</div>
            <div style={{ fontSize: '0.75rem', opacity: 0.9, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={styles.onlineDot} /> {tenant.statusText || DEFAULT_STATUS_TEXT}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div style={styles.thread}>
          {messages.map((m) => (
            <div
              key={m.id}
              style={{ ...styles.row, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}
            >
              <div
                style={{
                  ...styles.bubble,
                  ...(m.role === 'user'
                    ? { background: primaryColor, color: '#fff', borderBottomRightRadius: 4 }
                    : { background: '#f1f5f9', color: '#0f172a', borderBottomLeftRadius: 4 }),
                }}
              >
                {m.role === 'user' ? m.text : sanitizeAssistantText(m.text)}
              </div>
            </div>
          ))}

          {botTyping && (
            <div style={{ ...styles.row, justifyContent: 'flex-start' }}>
              <div style={{ ...styles.bubble, background: '#f1f5f9', color: '#94a3b8' }}>
                <span style={styles.typing}>
                  <i style={{ ...styles.dot, animationDelay: '0s' }} />
                  <i style={{ ...styles.dot, animationDelay: '0.15s' }} />
                  <i style={{ ...styles.dot, animationDelay: '0.3s' }} />
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div style={styles.inputBar}>
          <input
            style={styles.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type your message…"
            disabled={botTyping}
            autoComplete="off"
            aria-label="Your message"
          />
          <button
            type="button"
            onClick={send}
            disabled={botTyping || !input.trim()}
            style={{
              ...styles.sendBtn,
              background: botTyping || !input.trim() ? '#cbd5e1' : primaryColor,
              cursor: botTyping || !input.trim() ? 'not-allowed' : 'pointer',
            }}
            aria-label="Send"
          >
            ➤
          </button>
        </div>

        <p style={styles.privacy}>Your information is kept private and only used to contact you.</p>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes blink{0%,80%,100%{opacity:.25}40%{opacity:1}}`}</style>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
  },
  card: {
    background: '#ffffff',
    borderRadius: 20,
    boxShadow: '0 20px 60px rgba(79, 70, 229, 0.12), 0 4px 16px rgba(0,0,0,0.06)',
    width: '100%',
    maxWidth: 440,
    height: 'min(640px, 90vh)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: '1px solid #e8e4ff',
  },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', color: '#fff' },
  headerLogo: { height: 40, width: 40, borderRadius: 10, objectFit: 'contain', background: '#fff', padding: 3 },
  headerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 18,
  },
  onlineDot: { width: 8, height: 8, borderRadius: '50%', background: '#4ade80', display: 'inline-block' },
  thread: {
    flex: 1,
    overflowY: 'auto',
    padding: '18px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    background: '#fbfcfe',
  },
  row: { display: 'flex', width: '100%' },
  bubble: {
    maxWidth: '80%',
    padding: '9px 13px',
    borderRadius: 16,
    fontSize: '0.9rem',
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
  },
  typing: { display: 'inline-flex', gap: 4, alignItems: 'center', height: 12 },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#94a3b8',
    display: 'inline-block',
    animation: 'blink 1.2s infinite both',
  },
  inputBar: {
    display: 'flex',
    gap: 8,
    padding: '12px 14px',
    borderTop: '1px solid #eef2f7',
    background: '#fff',
  },
  input: {
    flex: 1,
    boxSizing: 'border-box',
    padding: '11px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 22,
    fontSize: '0.9rem',
    color: '#0f172a',
    background: '#fafbfc',
    outline: 'none',
  },
  sendBtn: {
    width: 44,
    height: 44,
    flexShrink: 0,
    border: 'none',
    borderRadius: '50%',
    color: '#fff',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  privacy: {
    textAlign: 'center',
    margin: 0,
    padding: '0 16px 12px',
    fontSize: '0.7rem',
    color: '#cbd5e1',
    background: '#fff',
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #4f46e5',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '40px auto',
  },
} satisfies Record<string, React.CSSProperties>;
